/* eslint-disable react-hooks/exhaustive-deps */
import DeleteModal from "components/DeleteModal";
import ImageModal from "components/ImageModal";
import { useTooltip } from "context/TooltipProvider";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  deleteGovernmental,
  deleteNonGovernmental,
  deleteVictim,
  fetchGovernmentalByIncidentRegistrationNumber,
  fetchNonGovernmentalByIncidentRegistrationNumber,
  fetchVictimByIncidentRegistrationNumber,
} from "services/lealIntityService";
import {
  CrimeReference,
  FILE_URL,
  Governmental,
  Investigation,
  NonGovernmental,
  Victim,
} from "types";
import {
  deleteIcon,
  editIcon,
  governmentalIcon,
  viewIcon,
} from "utils/constant";
import { constructFileUrl, errorHandler } from "utils/helper";
import VictimGovernmentalDetail from "./modals/VictimGovernentalDetail";
import VitimGovernmentalForm from "./modals/VictimGovernmentalForm";
import VictimNonGovernmentalDetail from "./modals/VictimNonGovernentalDetail";
import VitimNonGovernmentalForm from "./modals/VictimNonGovernmentalForm";
import VictimPersonalDetail from "./modals/VictimPersonalDetail";
import VictimPersonalForm from "./modals/VictimPersonalForm";

// Reusable Action Button Component
const ActionButton: React.FC<{
  onClick: () => void;
  children: React.ReactNode;
  variant?: "primary" | "secondary" | "outline";
  className?: string;
}> = ({ onClick, children, variant = "outline", className = "" }) => {
  const baseClasses =
    "px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 flex items-center gap-2";
  const variants = {
    primary: "bg-red-600 text-white hover:bg-red-700 shadow-sm",
    secondary: "bg-gray-600 text-white hover:bg-gray-700 shadow-sm",
    outline: "border border-gray-300 text-gray-700 hover:bg-gray-50",
  };

  return (
    <button
      className={`${baseClasses} ${variants[variant]} ${className}`}
      onClick={onClick}
    >
      {children}
    </button>
  );
};

type Props = {
  investigation: Investigation;
  incident?: CrimeReference;
  canManageIncidentDetail: boolean;
};

const VictimsTab: React.FC<Props> = ({
  investigation,
  incident,
  canManageIncidentDetail = false,
}) => {
  const { t } = useTranslation();
  const { showTooltip } = useTooltip();
  const [victims, setVictims] = useState<Victim[]>([]);
  const [governmental, setGovernmental] = useState<Governmental[]>([]);
  const [nonGovernmental, setNonGovernmental] = useState<NonGovernmental[]>([]);
  const [action, setAction] = useState<"Add" | "Edit">("Add");
  const [isImageModalOpen, setIsImageModalOpen] = React.useState(false);
  const [loading, setLoading] = useState(false);
  const [isOpenForm, setIsOpenForm] = React.useState({ type: "", open: false });
  const [isOpenDetail, setIsOpenDetail] = React.useState({
    type: "",
    open: false,
  });
  const [isOpenDelete, setIsOpenDelete] = React.useState({
    type: "",
    open: false,
  });
  const [selectedVictim, setSelectedVictim] = React.useState<Victim>();
  const [selectedGovernment, setSelectedGovernment] =
    React.useState<Governmental>();
  const [selectedNonGovernment, setSelectedNonGovernment] =
    React.useState<NonGovernmental>();

  const fetchVictimData = async () => {
    fetchVictimByIncidentRegistrationNumber(
      incident?.Incident_Registration_number || "",
    )
      .then(setVictims)
      .catch(console.error);
  };

  const fetchGovernmentalData = async () => {
    fetchGovernmentalByIncidentRegistrationNumber(
      incident?.Incident_Registration_number || "",
    )
      .then(setGovernmental)
      .catch(console.error);
  };

  const fetchNonGovernmentalData = async () => {
    fetchNonGovernmentalByIncidentRegistrationNumber(
      incident?.Incident_Registration_number || "",
    )
      .then(setNonGovernmental)
      .catch(console.error);
  };

  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetchVictimData(),
      fetchGovernmentalData(),
      fetchNonGovernmentalData(),
    ])
      .then(() => setLoading(false))
      .catch((error) => {
        setLoading(false);
        showTooltip(
          error?.response?.data?.detail || errorHandler(error),
          "error",
        );
      });
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <span className="text-2xl">👥</span>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {t("victim_information")}
              </h1>
              <p className="text-gray-600 mt-1">
                {t("manage_victim_information_for_incident")}
              </p>
            </div>
          </div>

          {canManageIncidentDetail && incident?.status !== "forwarded" && (
            <div className="flex flex-wrap gap-3">
              <ActionButton
                onClick={() => {
                  setAction("Add");
                  setIsOpenForm({ type: "Personal", open: true });
                }}
              >
                ➕ {t("add_personal")}
              </ActionButton>
              <ActionButton
                onClick={() => {
                  setAction("Add");
                  setIsOpenForm({ type: "Governmental", open: true });
                }}
              >
                ➕ {t("add_governmental")}
              </ActionButton>
              <ActionButton
                onClick={() => {
                  setAction("Add");
                  setIsOpenForm({ type: "Non-Governmental", open: true });
                }}
              >
                ➕ {t("add_non_governmental")}
              </ActionButton>
            </div>
          )}
        </div>
      </div>

      {victims.length === 0 &&
      governmental?.length === 0 &&
      nonGovernmental?.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
          <div className="text-center text-gray-500">
            {t("no_victims_found")}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6">
          {/* Personal Victims */}
          {victims.map((victim, index) => (
            <div
              key={index}
              className="bg-white border border-gray-200 rounded-xl shadow-sm p-6"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-4">
                  <img
                    src={(() => {
                      const doc = victim?.documents?.find(
                        (doc) => doc.document_type === "victim_picture",
                      );
                      return doc
                        ? constructFileUrl(doc, FILE_URL.VICTIM)
                        : require("../../../images/imageVector.png");
                    })()}
                    alt="User"
                    className="w-12 h-12 rounded-lg object-cover cursor-pointer"
                    onClick={() => {
                      setSelectedVictim(victim);
                      setIsImageModalOpen(true);
                    }}
                  />
                  <div>
                    <h3 className="font-semibold text-gray-900">
                      {`${victim?.first_name} ${victim?.middle_name} ${victim?.last_name}`}
                    </h3>
                    <div className="flex items-center gap-2 text-sm text-gray-600 mt-1">
                      <span>
                        {victim?.age} {t("years_old_label")}
                      </span>
                      <span>•</span>
                      <span>{victim?.education_level}</span>
                      <span>•</span>
                      <span>{victim?.nationality}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedVictim(victim);
                      setIsOpenDetail({ type: "Personal", open: true });
                    }}
                    className="p-2 text-gray-600 hover:text-blue-600 hover:bg-gray-50 rounded-lg transition-colors"
                    title={t("view_details_button")}
                  >
                    {viewIcon}
                  </button>
                  {canManageIncidentDetail &&
                    incident?.status !== "forwarded" && (
                      <>
                        <button
                          type="button"
                          onClick={() => {
                            setAction("Edit");
                            setSelectedVictim(victim);
                            setIsOpenForm({ type: "Personal", open: true });
                          }}
                          className="p-2 text-gray-600 hover:text-green-600 hover:bg-gray-50 rounded-lg transition-colors"
                          title={t("edit_victim")}
                        >
                          {editIcon}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedVictim(victim);
                            setIsOpenDelete({ type: "Personal", open: true });
                          }}
                          className="p-2 text-gray-600 hover:text-red-600 hover:bg-gray-50 rounded-lg transition-colors"
                          title={t("delete_victim")}
                        >
                          {deleteIcon}
                        </button>
                      </>
                    )}
                </div>
              </div>
            </div>
          ))}

          {/* Governmental Victims */}
          {governmental.map((government, index) => (
            <div
              key={index}
              className="bg-white border border-gray-200 rounded-xl shadow-sm p-6"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                    {governmentalIcon}
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">
                      {government?.governmental_type}
                      <span className="text-sm text-gray-500 ml-2">
                        ({t("governmental")})
                      </span>
                    </h3>
                    <div className="flex items-center gap-2 text-sm text-gray-600 mt-1">
                      <span>{government?.department}</span>
                      <span>•</span>
                      <span>{government?.office_name}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedGovernment(government);
                      setIsOpenDetail({ type: "Governmental", open: true });
                    }}
                    className="p-2 text-gray-600 hover:text-blue-600 hover:bg-gray-50 rounded-lg transition-colors"
                    title={t("view_details_button")}
                  >
                    {viewIcon}
                  </button>
                  {canManageIncidentDetail &&
                    incident?.status !== "forwarded" && (
                      <>
                        <button
                          type="button"
                          onClick={() => {
                            setAction("Edit");
                            setSelectedGovernment(government);
                            setIsOpenForm({ type: "Governmental", open: true });
                          }}
                          className="p-2 text-gray-600 hover:text-green-600 hover:bg-gray-50 rounded-lg transition-colors"
                          title={t("edit_governmental_victim")}
                        >
                          {editIcon}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedGovernment(government);
                            setIsOpenDelete({
                              type: "Governmental",
                              open: true,
                            });
                          }}
                          className="p-2 text-gray-600 hover:text-red-600 hover:bg-gray-50 rounded-lg transition-colors"
                          title={t("delete_governmental_victim")}
                        >
                          {deleteIcon}
                        </button>
                      </>
                    )}
                </div>
              </div>
            </div>
          ))}

          {/* Non-Governmental Victims */}
          {nonGovernmental.map((nonGovernment, index) => (
            <div
              key={index}
              className="bg-white border border-gray-200 rounded-xl shadow-sm p-6"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                    {governmentalIcon}
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">
                      {nonGovernment?.organization_name}
                      <span className="text-sm text-gray-500 ml-2">
                        ({t("non_governmental")})
                      </span>
                    </h3>
                    <div className="flex items-center gap-2 text-sm text-gray-600 mt-1">
                      <span>{nonGovernment?.non_governmental_type}</span>
                      <span>•</span>
                      <span>{nonGovernment?.registration_number}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedNonGovernment(nonGovernment);
                      setIsOpenDetail({ type: "Non-Governmental", open: true });
                    }}
                    className="p-2 text-gray-600 hover:text-blue-600 hover:bg-gray-50 rounded-lg transition-colors"
                    title={t("view_details_button")}
                  >
                    {viewIcon}
                  </button>
                  {canManageIncidentDetail &&
                    incident?.status !== "forwarded" && (
                      <>
                        <button
                          type="button"
                          onClick={() => {
                            setAction("Edit");
                            setSelectedNonGovernment(nonGovernment);
                            setIsOpenForm({
                              type: "Non-Governmental",
                              open: true,
                            });
                          }}
                          className="p-2 text-gray-600 hover:text-green-600 hover:bg-gray-50 rounded-lg transition-colors"
                          title={t("edit_non_governmental_victim")}
                        >
                          {editIcon}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedNonGovernment(nonGovernment);
                            setIsOpenDelete({
                              type: "Non-Governmental",
                              open: true,
                            });
                          }}
                          className="p-2 text-gray-600 hover:text-red-600 hover:bg-gray-50 rounded-lg transition-colors"
                          title={t("delete_non_governmental_victim")}
                        >
                          {deleteIcon}
                        </button>
                      </>
                    )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modals */}
      {isOpenForm.open && isOpenForm.type === "Personal" && (
        <VictimPersonalForm
          action={action}
          investigation={investigation}
          incident={incident}
          victim={action === "Edit" ? selectedVictim : undefined}
          isOpen={isOpenForm.open}
          onClose={(refetch) => {
            setIsOpenForm({ type: "", open: false });
            if (refetch) {
              fetchVictimData();
              fetchGovernmentalData();
              fetchNonGovernmentalData();
            }
          }}
        />
      )}
      {isOpenForm.open && isOpenForm.type === "Governmental" && (
        <VitimGovernmentalForm
          action={action}
          investigation={investigation}
          incident={incident}
          governmental={action === "Edit" ? selectedGovernment : undefined}
          isOpen={isOpenForm.open}
          onClose={(refetch) => {
            setIsOpenForm({ type: "", open: false });
            if (refetch) {
              fetchVictimData();
              fetchGovernmentalData();
              fetchNonGovernmentalData();
            }
          }}
        />
      )}
      {isOpenForm.open && isOpenForm.type === "Non-Governmental" && (
        <VitimNonGovernmentalForm
          action={action}
          investigation={investigation}
          incident={incident}
          nonGovernmental={
            action === "Edit" ? selectedNonGovernment : undefined
          }
          isOpen={isOpenForm.open}
          onClose={(refetch) => {
            setIsOpenForm({ type: "", open: false });
            if (refetch) {
              fetchVictimData();
              fetchGovernmentalData();
              fetchNonGovernmentalData();
            }
          }}
        />
      )}

      {/* Detail Modals */}
      {isOpenDetail.open && isOpenDetail.type === "Personal" && (
        <VictimPersonalDetail
          victim={selectedVictim}
          isOpen={isOpenDetail.open}
          onClose={() => setIsOpenDetail({ type: "", open: false })}
        />
      )}
      {isOpenDetail.open && isOpenDetail.type === "Governmental" && (
        <VictimGovernmentalDetail
          governmental={selectedGovernment}
          isOpen={isOpenDetail.open}
          onClose={() => setIsOpenDetail({ type: "", open: false })}
        />
      )}
      {isOpenDetail.open && isOpenDetail.type === "Non-Governmental" && (
        <VictimNonGovernmentalDetail
          nonGovernmental={selectedNonGovernment}
          isOpen={isOpenDetail.open}
          onClose={() => setIsOpenDetail({ type: "", open: false })}
        />
      )}

      {/* Delete Modal */}
      {(selectedVictim || selectedGovernment || selectedNonGovernment) && (
        <DeleteModal
          isOpen={isOpenDelete.open}
          onConfirm={() => {
            if (isOpenDelete.type === "Personal" && selectedVictim) {
              deleteVictim(selectedVictim)
                .then(() => {
                  showTooltip(t("successfully_deleted"), "success");
                  fetchVictimData();
                })
                .catch((error) => {
                  showTooltip(
                    error?.response?.data?.detail ||
                      errorHandler(error) ||
                      t("something_went_wrong_try_again"),
                    "error",
                  );
                });
            } else if (
              isOpenDelete.type === "Governmental" &&
              selectedGovernment
            ) {
              deleteGovernmental(selectedGovernment)
                .then(() => {
                  showTooltip(t("successfully_deleted"), "success");
                  fetchGovernmentalData();
                })
                .catch((error) => {
                  showTooltip(
                    error?.response?.data?.detail ||
                      errorHandler(error) ||
                      t("something_went_wrong_try_again"),
                    "error",
                  );
                });
            } else if (
              isOpenDelete.type === "Non-Governmental" &&
              selectedNonGovernment
            ) {
              deleteNonGovernmental(selectedNonGovernment)
                .then(() => {
                  showTooltip(t("successfully_deleted"), "success");
                  fetchNonGovernmentalData();
                })
                .catch((error) => {
                  showTooltip(
                    error?.response?.data?.detail ||
                      errorHandler(error) ||
                      t("something_went_wrong_try_again"),
                    "error",
                  );
                });
            }
          }}
          onCancel={() => {
            setIsOpenDelete({ type: "", open: false });
          }}
          title={t("delete_victim")}
          message={t("confirm_delete_victim")}
        />
      )}

      {/* Image Modal */}
      <ImageModal
        isOpen={isImageModalOpen}
        imageSrc={(() => {
          const doc = selectedVictim?.documents?.find(
            (doc) => doc.document_type === "victim_picture",
          );
          return doc
            ? constructFileUrl(doc, FILE_URL.VICTIM)
            : require("../../../images/imageVector.png");
        })()}
        label={
          selectedVictim?.first_name +
          " " +
          selectedVictim?.last_name +
          " " +
          selectedVictim?.last_name
        }
        onClose={() => setIsImageModalOpen(false)}
      />
    </div>
  );
};

export default VictimsTab;
